import { useMemo, useState } from 'react';
import { BarChart3, History, Scale, Zap } from 'lucide-react';
import {
  WORKLOAD_RANK_CLASSES,
  buildDriverWorkload,
  describeQuickTask,
} from '../../core/driver-workload';
import { formatAuditClock } from '../../core/audit-stamp';
import { hasPermission } from '../../core/permissions';
import { DriverWorkloadChart } from './DriverWorkloadChart';
import { QuickTaskModal } from './QuickTaskModal';
import { useQuickTasks } from './use-quick-tasks';
import type { AssignmentIndex } from '../../core/effective-status';
import type { Task } from '../../types/task';
import type { TaskTeam } from '../../types/assignment';
import type { Soldier } from '../../types/fleet';
import type { User } from '../../types/user';

interface DriverWorkloadSectionProps {
  user: User;
  soldiers: readonly Soldier[];
  tasks: readonly Task[];
  teams: readonly TaskTeam[];
  assignments: AssignmentIndex;
}

/** How many logged journeys the audit strip shows before it stops. */
const AUDIT_STRIP_LIMIT = 5;

/**
 * איזון עומס נהגים ומפקדי משימה & ניטור משימות — a section of its own on the
 * dashboard.
 *
 * A mission is credited to both seats it was executed from, נהג and
 * מפקד משימה, so the ranking answers "who has been out" rather than "who held a
 * steering wheel".
 *
 * Deliberately standalone, and deliberately *not* folded into the roster or the
 * driver-availability widget. Those two answer "who is here"; this one answers
 * the question that comes next and is much harder to eyeball — "who has already
 * done the most today, and who has barely moved". They are different questions
 * with different evidence behind them, and a manager handing out the next job
 * wants this one on its own.
 *
 * Everything on the screen is derived by `src/core/driver-workload.ts`; the
 * component counts nothing and orders nothing, exactly as `DashboardPage` counts
 * nothing of its own. Dexie is reached only through `./use-quick-tasks`.
 */
export function DriverWorkloadSection({
  user,
  soldiers,
  tasks,
  teams,
  assignments,
}: DriverWorkloadSectionProps) {
  const [now] = useState(() => Date.now());
  const [logging, setLogging] = useState(false);
  const { quickTasks, loading, log } = useQuickTasks(user);

  const workload = useMemo(
    () => buildDriverWorkload({ soldiers, teams, tasks, quickTasks, assignments, now }),
    [soldiers, teams, tasks, quickTasks, assignments, now],
  );

  // Logging a journey a driver carried out is the same authority as opening the
  // job in the first place — see `logQuickDriverTask()`, which enforces it at the
  // store as well, so hiding the button is a courtesy and not the guard.
  const canLog = hasPermission(user, 'CREATE_TASK');

  return (
    <section id="driver-workload" className="bg-panel border border-line rounded-2xl p-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Scale size={16} className="text-brand-500" aria-hidden />
            איזון עומס נהגים ומפקדי משימה &amp; ניטור משימות
          </h3>
          <p className="text-xs text-ink/60 mt-1">
            {workload.systemTotal} משימות מערכת ({workload.driverTotal} כנהג ·{' '}
            {workload.commanderTotal} כמפקד משימה) · {workload.quickTotal} רישומים מהירים ·{' '}
            {workload.rows.length} חיילים במצבת
          </p>
        </div>

        {canLog ? (
          <button
            type="button"
            onClick={() => setLogging(true)}
            className="h-11 px-4 rounded-xl bg-brand-500 text-white text-sm font-semibold flex items-center gap-2 shadow-sm hover:bg-brand-700 transition-colors"
          >
            <Zap size={18} aria-hidden />⚡ רישום משימה מהירה
          </button>
        ) : null}
      </header>

      {loading ? (
        <p className="text-sm text-ink/60 mt-4">טוען את פילוח העומס…</p>
      ) : (
        <>
          {/* ── Who to send next ────────────────────────────────────── */}
          {workload.nextUp.length > 0 ? (
            <p className="mt-3 text-sm rounded-xl border border-emerald-300 bg-emerald-50 text-emerald-800 p-3">
              זמינים למשימה הבאה:{' '}
              <span className="font-semibold">
                {workload.nextUp.map((row) => `${row.name} (${row.total})`).join(' · ')}
              </span>
            </p>
          ) : null}

          {/* ── The unified graph ───────────────────────────────────── */}
          <div className="mt-3">
            <h4 className="text-xs font-semibold text-ink/60 flex items-center gap-1.5 mb-2">
              <BarChart3 size={14} aria-hidden />
              סה״כ משימות שבוצעו לפי נהג ומפקד משימה
            </h4>
            <DriverWorkloadChart rows={workload.rows} />
          </div>

          {/* ── The ranked roster ───────────────────────────────────── */}
          {workload.rows.length > 0 ? (
            <ul className="mt-4 max-h-72 overflow-y-auto overscroll-contain rounded-xl border border-line divide-y divide-line">
              {workload.rows.map((row) => (
                <li
                  key={row.soldierId}
                  className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{row.displayName}</span>
                    <span className="block text-xs text-ink/50">
                      {row.systemTasks} מערכת
                      {row.seatBreakdown ? ` (${row.seatBreakdown})` : ''} · {row.quickTasks} מהיר ·{' '}
                      {row.availabilityLabel}
                    </span>
                  </span>
                  <span className="shrink-0 flex items-center gap-2">
                    <span
                      className={`text-xs rounded-full border px-2 py-0.5 ${
                        WORKLOAD_RANK_CLASSES[row.rank]
                      }`}
                    >
                      {row.rankLabel}
                    </span>
                    <span className="font-bold tabular-nums text-base w-6 text-center">
                      {row.total}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          ) : null}

          {/* ── The audit trail on the quick log ────────────────────── */}
          {quickTasks.length > 0 ? (
            <>
              <h4 className="text-xs font-semibold text-ink/60 flex items-center gap-1.5 mt-4 mb-2">
                <History size={14} aria-hidden />
                רישומים מהירים אחרונים
              </h4>
              <ul className="space-y-1.5 text-xs text-ink/70">
                {quickTasks.slice(0, AUDIT_STRIP_LIMIT).map((entry) => (
                  <li key={entry.id} className="rounded-xl border border-line p-2.5">
                    {describeQuickTask(entry, formatAuditClock(entry.createdAt, now))}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-xs text-ink/50 mt-4">
              עדיין לא נרשמו משימות מהירות. כל רישום נשמר עם שם הרושם והשעה.
            </p>
          )}
        </>
      )}

      {logging ? (
        <QuickTaskModal
          soldiers={soldiers}
          assignments={assignments}
          onClose={() => setLogging(false)}
          onSubmit={log}
        />
      ) : null}
    </section>
  );
}
